import {
  useState,
  forwardRef,
  CSSProperties,
  PropsWithChildren,
  ReactNode,
} from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { List, ListItem } from '../list';
import { DraggableItem } from '../../drag-and-drop';

import styles from './list-sortable.module.scss';

type SortableListProps<T> = {
  items: T[];
  renderItem: (item: T) => ReactNode;
  handleDragEnd: (event: DragEndEvent) => void;
};

export const SortableList = <T extends DraggableItem>({
  items,
  renderItem,
  handleDragEnd,
}: SortableListProps<T>) => {
  const [activeItem, setActiveItem] = useState<T>();
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
        delay: 200,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;

    const activeItem = items.find((item) => item.id === active.id);
    setActiveItem(activeItem);
  };

  const onDragEnd = (event: DragEndEvent) => {
    setActiveItem(undefined);
    handleDragEnd(event);
  };

  const onDragCancel = () => {
    setActiveItem(undefined);
  };

  const OverlaySortableItem = forwardRef<
    HTMLDivElement,
    SortableListItemProps<T>
  >(({ id, item, renderItem }, ref) => {
    const { attributes, listeners, transform, transition } = useSortable({
      id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <DraggableListItem
        ref={ref}
        style={style}
        className={styles['overlayDraggable']}
        {...attributes}
        {...listeners}
      >
        {renderItem(item)}
      </DraggableListItem>
    );
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <List
          className={styles['sortableList']}
          dataSource={items}
          renderItem={(item) => (
            <ListItem key={item.id} className={styles['listItem']}>
              <SortableListItem
                id={item.id}
                item={item}
                renderItem={renderItem}
              />
            </ListItem>
          )}
        />
      </SortableContext>
      <DragOverlay>
        {activeItem && (
          <OverlaySortableItem
            id={activeItem.id}
            item={activeItem}
            renderItem={renderItem}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

type SortableListItemProps<T> = {
  id: UniqueIdentifier;
  item: T;
  renderItem: (item: T) => ReactNode;
};

const SortableListItem = <T,>({
  id,
  item,
  renderItem,
}: SortableListItemProps<T & DraggableItem>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    opacity: isDragging ? 0 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <DraggableListItem
      ref={setNodeRef}
      style={style}
      className={styles['draggable']}
      {...attributes}
      {...listeners}
    >
      {renderItem(item)}
    </DraggableListItem>
  );
};

const DraggableListItem = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{ style?: CSSProperties; className?: string }>
>(({ children, ...props }, ref) => {
  return (
    <div ref={ref} {...props}>
      {children}
    </div>
  );
});
